<template>
  <PageHeader
    breadcrumb
    :items="breadcrumbItems"
  >
    <template #right>
      <div class="flex flex-wrap gap-2">
        <Button
          label="Back to Templates"
          severity="secondary"
          outlined
          icon="arrow-left"
          router-link="/settings/communication-templates"
        />
        <Button
          v-if="$can('manage_communication_settings')"
          label="Cancel"
          severity="secondary"
          outlined
          @click="handleCancel"
        />
        <Button
          v-if="$can('manage_communication_settings')"
          :label="isEditingTemplate ? 'Save Template' : 'Create Template'"
          icon="save"
          :is-loading="isSubmittingTemplate"
          @click="submitTemplate"
        />
      </div>
    </template>
  </PageHeader>

  <div
    v-if="isLoadingInitialData"
    class="grid gap-6 xl:grid-cols-[22rem_minmax(0,1fr)]"
  >
    <Card><CardBody>Loading template settings...</CardBody></Card>
    <Card><CardBody>Loading editor...</CardBody></Card>
  </div>

  <div
    v-else
    class="grid gap-6 xl:grid-cols-[22rem_minmax(0,1fr)]"
  >
    <Card>
      <CardBody class="space-y-5">
        <div>
          <div class="text-lg font-semibold">{{ isEditingTemplate ? 'Template Settings' : 'New Template' }}</div>
          <div class="text-sm text-tertiary">Configure the template details, then write the message on the right.</div>
        </div>

        <FormGroup
          label="Name"
          :is-required="v$.name.required?.$invalid"
        >
          <Input
            v-model="nameValue"
            :has-error="v$.name.$error"
          />
          <InputError :has-error="v$.name.$error">{{ v$.name.$errors[0]?.$message }}</InputError>
        </FormGroup>

        <FormGroup
          label="Used For"
          :is-required="v$.usedFor.required?.$invalid"
        >
          <Select
            v-model="usedForValue"
            :options="templateUsageOptions"
            value-key="value"
            label-key="label"
            :has-error="v$.usedFor.$error"
          />
          <InputError :has-error="v$.usedFor.$error">{{ v$.usedFor.$errors[0]?.$message }}</InputError>
        </FormGroup>

        <FormGroup
          label="Type"
          :is-required="v$.type.required?.$invalid"
        >
          <Select
            v-model="typeValue"
            :options="templateTypeOptions"
            value-key="value"
            label-key="label"
            :has-error="v$.type.$error"
          />
          <InputError :has-error="v$.type.$error">{{ v$.type.$errors[0]?.$message }}</InputError>
        </FormGroup>

        <FormGroup label="Category">
          <Input
            v-model="categoryValue"
            :has-error="v$.category.$error"
          />
          <InputError :has-error="v$.category.$error">{{ v$.category.$errors[0]?.$message }}</InputError>
        </FormGroup>

        <FormGroup label="Dealerships">
          <Select
            v-model="editorState.dealerships"
            :options="dealershipOptions"
            value-key="value"
            label-key="label"
            :chips="false"
            mode="multiple"
            placeholder="Leave empty for a global template"
          />
          <div class="mt-2 text-sm text-tertiary">Leave empty to make this template available globally.</div>
        </FormGroup>

        <FormGroup label="Options">
          <Checkbox
            v-model="editorState.isDefault"
            label="Set as default template"
          />
        </FormGroup>
      </CardBody>
    </Card>

    <Card>
      <CardBody class="space-y-5">
        <div class="space-y-1">
          <div class="text-lg font-semibold">Message Content</div>
          <div class="text-sm text-tertiary">
            Add placeholders with one click and author email templates directly as HTML.
          </div>
        </div>

        <FormGroup
          v-if="editorState.type === 'email'"
          label="Subject"
          :is-required="v$.subject.requiredIfEmail?.$invalid"
        >
          <div class="space-y-3">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div class="text-sm text-tertiary">Use placeholders in the subject line when needed.</div>
              <CommunicationTemplatePlaceholderPicker
                :placeholders="availablePlaceholderOptions"
                @select="insertSubjectPlaceholder"
              />
            </div>
            <input
              ref="subjectElement"
              v-model="subjectValue"
              type="text"
              class="form-control w-full"
              :class="{ 'has-error': v$.subject.$error }"
              placeholder="Enter an email subject"
            />
          </div>
          <InputError :has-error="v$.subject.$error">{{ v$.subject.$errors[0]?.$message }}</InputError>
        </FormGroup>

        <FormGroup
          :label="editorState.type === 'sms' ? 'SMS Body' : 'Email Body'"
          :is-required="currentBodyHasError"
        >
          <div
            v-if="editorState.type === 'sms'"
            class="space-y-3"
          >
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div class="text-sm text-tertiary">SMS templates stay as plain text and can still use placeholders.</div>
              <CommunicationTemplatePlaceholderPicker
                :placeholders="availablePlaceholderOptions"
                @select="insertSmsPlaceholder"
              />
            </div>
            <textarea
              ref="smsBodyElement"
              v-model="editorState.body"
              class="form-control w-full min-h-72"
              :class="{ 'has-error': v$.body.$error }"
              placeholder="Write your SMS template here..."
            ></textarea>
          </div>

          <CommunicationTemplateEmailEditor
            v-else
            v-model:html-value="editorState.body"
            :placeholders="availablePlaceholderOptions"
            :disabled="isHtmlBodyReadOnly"
            :has-error="currentBodyHasError"
            :error-message="currentBodyErrorMessage"
          >
            <div
              v-if="isHtmlBodyReadOnly"
              class="rounded-md border border-secondary bg-secondary p-3 text-sm text-tertiary"
            >
              Marketing email HTML is read-only because these templates use styling that the rich text editor cannot safely preserve.
            </div>
          </CommunicationTemplateEmailEditor>

          <InputError
            v-if="editorState.type === 'sms'"
            :has-error="v$.body.$error"
          >
            {{ v$.body.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
      </CardBody>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'

// libraries
import Swal from 'sweetalert2'
import useVuelidate from '@vuelidate/core'
import { helpers, maxLength, required } from '@vuelidate/validators'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'

// components
import Button from '@/components/button/Button.vue'
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import Checkbox from '@/components/forms/Checkbox.vue'
import CommunicationTemplateEmailEditor from '@/components/unique/communications/CommunicationTemplateEmailEditor.vue'
import CommunicationTemplatePlaceholderPicker from '@/components/unique/communications/CommunicationTemplatePlaceholderPicker.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'
import PageHeader from '@/components/page/PageHeader.vue'
import Select from '@/components/forms/Select.vue'

// composables
import useApiRequest from '@/composables/useApiRequest'

// stores
import { useAuthStore } from '@/stores/AuthStore'

// types
import type { Dealership } from '@/types/domain/dealership'
import type {
  CommunicationTemplate,
  CommunicationTemplateType,
  CommunicationTemplateUsedFor
} from '@/types/domain/communicationTemplate'

// utilities
import {
  cloneCommunicationTemplateEditorState,
  hasMeaningfulHtml,
  hasTextValue,
  insertTokenAtSelection,
  normalizeCommunicationTemplateEditorState,
  serializeCommunicationTemplatePayload
} from '@/utilities/communications/templateEditor'
import { alertToast } from '@/utilities/notification'
import { getCommunicationTemplatePlaceholderOptions } from '@/utilities/communications/templatePlaceholders'

/**
 * Select options for template usage.
 */
const templateUsageOptions: Array<{ value: CommunicationTemplateUsedFor; label: string }> = [
  { value: 'communication', label: 'Communication' },
  { value: 'marketing', label: 'Marketing' }
]

/**
 * Select options for template delivery type.
 */
const templateTypeOptions: Array<{ value: CommunicationTemplateType; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' }
]

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

/**
 * Template id parsed from the current route when editing.
 */
const templateId = computed<number | null>(() => {
  const rawId = route.params.id
  const parsedId = Number(rawId)

  // Only numeric ids represent persisted templates.
  return Number.isInteger(parsedId) ? parsedId : null
})

/**
 * Returns true when the page is editing an existing template.
 */
const isEditingTemplate = computed(() => templateId.value !== null)

/**
 * Current editor state for the full-page form.
 */
const editorState = ref(normalizeCommunicationTemplateEditorState({
  id: 0,
  usedFor: 'communication',
  name: null,
  type: 'email',
  category: null,
  subject: null,
  body: '',
  isDefault: false,
  dealerships: [],
  createdAt: null,
  updatedAt: null
}))

/**
 * Snapshot used for unsaved-change checks.
 */
const initialEditorState = ref(cloneCommunicationTemplateEditorState(editorState.value))

/**
 * Tracks page-level loading while fetching editor data.
 */
const isLoadingInitialData = ref(true)

/**
 * Input refs used for cursor-aware placeholder insertion.
 */
const subjectElement = ref<HTMLInputElement | null>(null)
const smsBodyElement = ref<HTMLTextAreaElement | null>(null)

/**
 * Dealerships shown in the scope multi-select.
 */
const dealerships = ref<Dealership[]>([])

const dealershipsRequest = useApiRequest()
const templateDetailsRequest = useApiRequest()
const createTemplateRequest = useApiRequest()
const updateTemplateRequest = useApiRequest()

/**
 * Returns true while the template save request is in flight.
 */
const isSubmittingTemplate = computed(() => {
  return createTemplateRequest.isLoading.value || updateTemplateRequest.isLoading.value
})

/**
 * Human-readable breadcrumb trail for the page header.
 */
const breadcrumbItems = computed(() => {
  return [
    { label: 'Settings', to: '/settings' },
    { label: 'Communications' },
    { label: 'Templates', to: '/settings/communication-templates' },
    { label: isEditingTemplate.value ? editorState.value.name || 'Edit Template' : 'New Template' }
  ]
})

/**
 * Dealership select options derived from the loaded dealership list.
 */
const dealershipOptions = computed(() => {
  return dealerships.value.map(dealership => ({
    value: dealership.id,
    label: dealership.displayName || dealership.name
  }))
})

/**
 * Placeholder options filtered to the currently selected template type.
 */
const availablePlaceholderOptions = computed(() => {
  return getCommunicationTemplatePlaceholderOptions(editorState.value.type)
})

/**
 * Alias used so form inputs can bind to string-only values.
 */
const nameValue = computed({
  get: () => editorState.value.name ?? '',
  set: value => {
    editorState.value.name = value
  }
})

/**
 * Alias used so optional category input can bind to a string.
 */
const categoryValue = computed({
  get: () => editorState.value.category ?? '',
  set: value => {
    editorState.value.category = value
  }
})

/**
 * Alias used so the select component can bind without passing null.
 */
const usedForValue = computed({
  get: () => editorState.value.usedFor ?? '',
  set: value => {
    editorState.value.usedFor = value ? (value as CommunicationTemplateUsedFor) : null
  }
})

/**
 * Alias used so the type select can bind without passing null.
 */
const typeValue = computed({
  get: () => editorState.value.type ?? '',
  set: value => {
    editorState.value.type = value ? (value as CommunicationTemplateType) : null
  }
})

/**
 * Alias used so the native subject input keeps cursor positioning support.
 */
const subjectValue = computed({
  get: () => editorState.value.subject ?? '',
  set: value => {
    editorState.value.subject = value
  }
})

/**
 * Returns true when the current editor state differs from the loaded snapshot.
 */
const hasUnsavedChanges = computed(() => {
  return JSON.stringify(editorState.value) !== JSON.stringify(initialEditorState.value)
})

/**
 * Normalizes Vuelidate messages into plain strings for child components.
 */
function getValidationMessage(message: unknown): string {
  return typeof message === 'string' ? message : String(message ?? '')
}

/**
 * Validation rules adapt to the currently selected template mode and type.
 */
const validationRules = computed(() => ({
  usedFor: {
    required: helpers.withMessage('Used for is required', required)
  },
  name: {
    required: helpers.withMessage('Name is required', value => hasTextValue(value)),
    maxLength: helpers.withMessage('Name must be 50 characters or less', maxLength(50))
  },
  type: {
    required: helpers.withMessage('Type is required', required)
  },
  category: {
    maxLength: helpers.withMessage('Category must be 20 characters or less', maxLength(20))
  },
  subject: {
    requiredIfEmail: helpers.withMessage('Subject is required for email templates', value => {
      // Email templates always need a subject line.
      return editorState.value.type !== 'email' || hasTextValue(value)
    }),
    maxLength: helpers.withMessage('Subject must be 200 characters or less', maxLength(200))
  },
  body: {
    required: helpers.withMessage('Body is required', value => {
      if (editorState.value.type === 'sms') {
        return hasTextValue(value)
      }

      // Email templates are always authored as HTML.
      return hasMeaningfulHtml(typeof value === 'string' ? value : null)
    }),
    maxLength: helpers.withMessage('Body must be 5000 characters or less', value => {
      if (editorState.value.type === 'sms') {
        return String(value ?? '').length <= 5000
      }

      // Measure visible email text length rather than markup length.
      return String(value ?? '').replace(/<[^>]+>/g, '').length <= 5000
    })
  },
  isDefault: {},
  dealerships: {}
}))

const v$ = useVuelidate(validationRules, editorState, { $stopPropagation: true })

/**
 * Returns true when the active body field currently has a validation error.
 */
const currentBodyHasError = computed(() => {
  return v$.value.body.$error
})

/**
 * Returns the active body validation message.
 */
const currentBodyErrorMessage = computed(() => {
  return getValidationMessage(v$.value.body.$errors[0]?.$message)
})

/**
 * Returns true when the HTML email body should be protected from rich editor changes.
 */
const isHtmlBodyReadOnly = computed(() => {
  const hasAdminRole = authStore.roles.some(role => Number(role) === 1)

  // Only admin users can edit marketing email HTML body content.
  return editorState.value.usedFor === 'marketing' && editorState.value.type === 'email' && !hasAdminRole
})

/**
 * Loads the dealership list used to scope templates.
 */
async function fetchDealerships(): Promise<void> {
  try {
    const response = await dealershipsRequest.send<Dealership[]>({
      endpoint: '/v1/dealerships',
      method: 'GET',
      params: {
        showAll: true,
        showDisabled: true
      }
    })

    if (response.success && response.data) {
      dealerships.value = response.data
      return
    }

    alertToast('Error', response.message || 'Failed to fetch dealerships', 'error')
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch dealerships'
    alertToast('Error', message, 'error')
  }
}

/**
 * Loads an existing template into the new full-page editor.
 */
async function fetchTemplate(): Promise<void> {
  if (!isEditingTemplate.value || templateId.value === null) {
    initialEditorState.value = cloneCommunicationTemplateEditorState(editorState.value)
    return
  }

  try {
    const response = await templateDetailsRequest.send<CommunicationTemplate>({
      endpoint: `/v1/comms/templates/${templateId.value}`,
      method: 'GET'
    })

    if (!response.success || !response.data) {
      alertToast('Error', response.message || 'Failed to load template', 'error')
      await router.push('/settings/communication-templates')
      return
    }

    editorState.value = normalizeCommunicationTemplateEditorState(response.data)
    initialEditorState.value = cloneCommunicationTemplateEditorState(editorState.value)
    v$.value.$reset()
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load template'
    alertToast('Error', message, 'error')
    await router.push('/settings/communication-templates')
  }
}

/**
 * Loads all page data needed for the editor.
 */
async function loadEditor(): Promise<void> {
  isLoadingInitialData.value = true

  try {
    await Promise.all([fetchDealerships(), fetchTemplate()])
  } finally {
    isLoadingInitialData.value = false
  }
}

/**
 * Saves a new or existing template using the shared serialization rules.
 */
async function submitTemplate(): Promise<void> {
  const isFormValid = await v$.value.$validate()

  if (!isFormValid) {
    return
  }

  const payload = JSON.stringify(serializeCommunicationTemplatePayload(editorState.value))

  try {
    if (isEditingTemplate.value && templateId.value !== null) {
      const response = await updateTemplateRequest.send<CommunicationTemplate>({
        endpoint: `/v1/comms/templates/${templateId.value}`,
        method: 'PATCH',
        data: payload
      })

      if (!response.success) {
        alertToast('Error', response.message || 'Failed to update template', 'error')
        return
      }

      alertToast('Success!', 'Template updated', 'success')
    } else {
      const response = await createTemplateRequest.send<CommunicationTemplate>({
        endpoint: '/v1/comms/templates',
        method: 'POST',
        data: payload
      })

      if (!response.success) {
        alertToast('Error', response.message || 'Failed to create template', 'error')
        return
      }

      alertToast('Success!', 'Template created', 'success')
    }

    initialEditorState.value = cloneCommunicationTemplateEditorState(editorState.value)
    await router.push('/settings/communication-templates')
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to save template'
    alertToast('Error', message, 'error')
  }
}

/**
 * Asks for confirmation before discarding unsaved changes.
 */
async function confirmDiscardChanges(): Promise<boolean> {
  if (!hasUnsavedChanges.value) {
    return true
  }

  const confirmation = await Swal.fire({
    title: 'Discard unsaved changes?',
    text: 'Your edits will be lost if you leave this page.',
    icon: 'warning',
    reverseButtons: true,
    showCancelButton: true,
    confirmButtonText: 'Discard changes',
    cancelButtonText: 'Keep editing'
  })

  return confirmation.isConfirmed
}

/**
 * Handles explicit cancel actions from the page header.
 */
async function handleCancel(): Promise<void> {
  const shouldDiscard = await confirmDiscardChanges()

  if (!shouldDiscard) {
    return
  }

  await router.push('/settings/communication-templates')
}

/**
 * Inserts a placeholder token into the subject line at the current cursor position.
 */
function insertSubjectPlaceholder(token: string): void {
  const element = subjectElement.value
  const sourceValue = editorState.value.subject ?? ''
  const selectionStart = element?.selectionStart ?? sourceValue.length
  const selectionEnd = element?.selectionEnd ?? sourceValue.length
  const result = insertTokenAtSelection(sourceValue, token, selectionStart, selectionEnd)

  editorState.value.subject = result.value

  requestAnimationFrame(() => {
    element?.focus()
    element?.setSelectionRange(result.cursorPosition, result.cursorPosition)
  })
}

/**
 * Inserts a placeholder token into the SMS body textarea at the current cursor position.
 */
function insertSmsPlaceholder(token: string): void {
  const element = smsBodyElement.value
  const selectionStart = element?.selectionStart ?? editorState.value.body.length
  const selectionEnd = element?.selectionEnd ?? editorState.value.body.length
  const result = insertTokenAtSelection(editorState.value.body, token, selectionStart, selectionEnd)

  editorState.value.body = result.value

  requestAnimationFrame(() => {
    element?.focus()
    element?.setSelectionRange(result.cursorPosition, result.cursorPosition)
  })
}

/**
 * Warns the browser before reload when the editor has unsaved changes.
 */
function handleBeforeUnload(event: BeforeUnloadEvent): void {
  if (!hasUnsavedChanges.value) {
    return
  }

  event.preventDefault()
  event.returnValue = ''
}

onBeforeRouteLeave(async (_to, _from, next) => {
  const shouldDiscard = await confirmDiscardChanges()
  next(shouldDiscard)
})

watch(
  () => route.fullPath,
  async () => {
    editorState.value = normalizeCommunicationTemplateEditorState({
      id: 0,
      usedFor: 'communication',
      name: null,
      type: 'email',
      category: null,
      subject: null,
      body: '',
      isDefault: false,
      dealerships: [],
      createdAt: null,
      updatedAt: null
    })
    initialEditorState.value = cloneCommunicationTemplateEditorState(editorState.value)
    v$.value.$reset()
    await loadEditor()
  },
  { immediate: true }
)

window.addEventListener('beforeunload', handleBeforeUnload)

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})
</script>
